import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import {
  ValidatedInput,
  ValidatedSelect,
  FormGroup,
} from "../ui/validated-input";
import { Building, Upload } from "lucide-react";
import { useCountryConfig } from "../../hooks/useCountryConfig";

// Fallback list used while country config is loading
const DEFAULT_LEGAL_FORMS = [
  { value: "SARL", label: "SARL" },
  { value: "SA", label: "SA" },
  { value: "OTHER", label: "Autre" },
];

const PAYROLL_COUNTRIES = [
  { value: "TN", label: "🇹🇳 Tunisie (TND)" },
  { value: "MA", label: "🇲🇦 Maroc (MAD)" },
  { value: "FR", label: "🇫🇷 France (EUR)" },
  { value: "QA", label: "🇶🇦 Qatar (QAR)" },
  { value: "SA", label: "🇸🇦 Arabie Saoudite (SAR)" },
];

const CompanyIdentityCard = ({
  company,
  getFieldProps,
  updateField,
  isEditing,
  isValidating,
  errors,
  capitalSocialDisplay,
  handleCapitalSocialChange,
  handleCapitalSocialBlur,
  handleLogoUpload,
}) => {
  const { t } = useTranslation();
  const countryCode = company?.country_code || "TN";
  const { config } = useCountryConfig(countryCode);

  // Dynamic legal forms from country config, with static fallback
  const legalForms = config?.legal_forms
    ? config.legal_forms.map((f) => ({ value: f, label: f }))
    : DEFAULT_LEGAL_FORMS;

  // Primary legal identifier
  const legalIdField = config?.legal_identifier?.field || "matricule_fiscal";
  const legalIdLabel = config?.legal_identifier?.label_fr || "Identifiant Fiscal";
  const legalIdPlaceholder = config?.legal_identifier?.placeholder || "";

  // Secondary identifiers
  const secondaryIds = config?.secondary_identifiers || [];

  const resolveLogoUrl = (logoUrl) => {
    if (!logoUrl || !logoUrl.trim()) return null;
    if (logoUrl.startsWith("/uploads/")) {
      return process.env.NODE_ENV === "development"
        ? `${process.env.REACT_APP_API_URL || "http://localhost:8000"}${logoUrl}`
        : logoUrl;
    }
    return logoUrl;
  };

  const fullLogoUrl = resolveLogoUrl(company.logo_url);
  const urlWithCache = fullLogoUrl
    ? fullLogoUrl + (fullLogoUrl.includes("?") ? "&" : "?") + `t=${Date.now()}`
    : null;

  return (
    <Card>
      <CardHeader className="pb-5">
        <CardTitle className="flex items-center gap-2.5 text-sm font-semibold" style={{ color: "var(--lm-ink)" }}>
          <div className="p-1.5 rounded-md" style={{ backgroundColor: "rgba(94,106,210,0.10)" }}>
            <Building className="h-4 w-4" style={{ color: "#5e6ad2" }} />
          </div>
          <span>{t("companyConfig.sections.identity")}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <FormGroup>
          <div className="flex items-center space-x-6 p-4 bg-primary/5 rounded-xl border border-primary/20">
            <Avatar
              className="h-20 w-20"
              style={{ border: "1px solid var(--lm-border)" }}
              key={`avatar-${company.logo_url?.length || 0}`}
            >
              {urlWithCache ? (
                <AvatarImage
                  src={urlWithCache}
                  alt={t("companyConfig.logoAlt")}
                  key={`logo-${company.logo_url}`}
                />
              ) : (
                <AvatarFallback className="text-white text-lg font-bold" style={{ backgroundColor: "#5e6ad2" }}>
                  {company.legal_name
                    ? company.legal_name.split(" ").map((w) => w.charAt(0)).join("").substring(0, 3).toUpperCase()
                    : "STS"}
                </AvatarFallback>
              )}
            </Avatar>
            {isEditing && (
              <div className="space-y-2">
                <Label htmlFor="logo" className="cursor-pointer">
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                    className="transition-colors"
                  >
                    <span>
                      <Upload className="mr-2 h-4 w-4 text-blue-600" />
                      {t("companyConfig.actions.changeLogo")}
                    </span>
                  </Button>
                </Label>
                <input id="logo" type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
                <p className="text-xs text-muted-foreground">{t("companyConfig.help.logoFormat")}</p>
              </div>
            )}
          </div>

          <ValidatedInput
            {...getFieldProps("legal_name")}
            id="legal_name"
            label={t("companyConfig.labels.companyName")}
            placeholder={t("companyConfig.placeholders.companyName")}
            disabled={!isEditing}
            required
            isValidating={isValidating}
            className="focus:ring-primary focus:border-primary"
          />

          <ValidatedInput
            {...getFieldProps("trade_name")}
            id="trade_name"
            label={t("companyConfig.labels.tradeName")}
            placeholder={t("companyConfig.placeholders.tradeName")}
            disabled={!isEditing}
            isValidating={isValidating}
            className="focus:ring-primary focus:border-primary"
          />

          {/* Primary legal identifier — dynamic per country */}
          <ValidatedInput
            {...getFieldProps(legalIdField)}
            id={legalIdField}
            label={legalIdLabel}
            placeholder={legalIdPlaceholder}
            disabled={!isEditing}
            isValidating={isValidating}
            className="focus:ring-primary focus:border-primary"
          />

          {/* Secondary identifiers — dynamic (SIREN/APE for FR, IF/RC for MA…) */}
          {secondaryIds.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              {secondaryIds.map((sid) => (
                <ValidatedInput
                  key={sid.field}
                  {...getFieldProps(sid.field)}
                  id={sid.field}
                  label={sid.label_fr}
                  placeholder={sid.placeholder || ""}
                  disabled={!isEditing}
                  isValidating={isValidating}
                  className="focus:ring-primary focus:border-primary"
                />
              ))}
            </div>
          )}

          <ValidatedSelect
            value={company.legal_form || "SARL"}
            onValueChange={(value) => updateField("legal_form", value)}
            id="legal_form"
            label={t("companyConfig.labels.legalForm")}
            placeholder={t("companyConfig.actions.select")}
            options={legalForms}
            disabled={!isEditing}
            isValidating={isValidating}
          />

          <ValidatedSelect
            value={company.country_code || "TN"}
            onValueChange={(value) => updateField("country_code", value)}
            id="country_code"
            label={t("companyConfig.payrollCountryLabel")}
            placeholder={t("companyConfig.payrollCountryPlaceholder")}
            options={PAYROLL_COUNTRIES}
            disabled={!isEditing}
            isValidating={isValidating}
            helpText={t("companyConfig.payrollCountryHelp")}
          />

          {["QA", "SA"].includes(countryCode) && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="wps_employer_id" className="text-sm font-medium text-foreground">
                  {countryCode === "QA" ? "Employer EID (WPS/QCB)" : "N° établissement MOL (WPS)"}
                </Label>
                <Input
                  id="wps_employer_id"
                  type="text"
                  value={company.wps_employer_id || ""}
                  onChange={(e) => updateField("wps_employer_id", e.target.value)}
                  disabled={!isEditing}
                  placeholder="Identifiant employeur WPS"
                  className="focus:ring-primary focus:border-primary"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="wps_bank_short_name" className="text-sm font-medium text-foreground">
                  Banque émettrice (WPS)
                </Label>
                <Input
                  id="wps_bank_short_name"
                  type="text"
                  value={company.wps_bank_short_name || ""}
                  onChange={(e) => updateField("wps_bank_short_name", e.target.value)}
                  disabled={!isEditing}
                  placeholder="Code / nom court de la banque"
                  className="focus:ring-primary focus:border-primary"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="capital_social" className="text-sm font-medium text-foreground">
                {t("companyConfig.labels.shareCapital")}
              </Label>
              <div className="relative">
                <Input
                  id="capital_social"
                  type="text"
                  value={capitalSocialDisplay}
                  onChange={handleCapitalSocialChange}
                  onBlur={handleCapitalSocialBlur}
                  disabled={!isEditing}
                  placeholder={config?.currency?.code ? `400,000 ${config.currency.code}` : t("companyConfig.placeholders.shareCapital")}
                  className="focus:ring-primary focus:border-primary"
                />
                {errors.capital_social && (
                  <p className="text-sm text-red-600 mt-1">{errors.capital_social}</p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {config?.currency?.code
                  ? `Capital (format: 400,000 ${config.currency.code})`
                  : t("companyConfig.help.shareCapital")}
              </p>
            </div>
          </div>
        </FormGroup>
      </CardContent>
    </Card>
  );
};

export default CompanyIdentityCard;
